import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid, Card, MenuItem,
  CircularProgress, Stack, IconButton,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  createDailyTaskAction,
  clearCreateDailyTaskAction,
  getAllUserAction,
} from "../../store/actions";

const FIELD_STYLE = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
  fontFamily: "DM Sans, sans-serif",
};

export default function CreateTask() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userDetails = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("userDetails")); }
    catch { return null; }
  }, []);

  const currentUserId = userDetails?.id || "";
  const basePath = location.pathname.startsWith("/masterData")
    ? "/masterData"
    : location.pathname.startsWith("/amrm")
    ? "/amrm"
    : "/SOorMDO";

  const { loading, successData, error, errorInfo } = useSelector(
    (state) => state.createDailyTaskReducer || {}
  );
  const { successData: usersData } = useSelector((state) => state.getAllUserReducer || {});

  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const [form, setForm] = useState({
    task_title: "",
    assigned_to_user_id: "",
    task_date: "",
    task_description: "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    dispatch(getAllUserAction());
    return () => { dispatch(clearCreateDailyTaskAction()); };
  }, [dispatch]);

  useEffect(() => {
    if (successData) {
      toast.success("Task assigned successfully!");
      dispatch(clearCreateDailyTaskAction());
      navigate(`${basePath}/dailyTask`);
    }
  }, [successData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      toast.error(errorInfo || "Failed to assign task.");
      dispatch(clearCreateDailyTaskAction());
    }
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  }

  function validate() {
    const errs = {};
    if (!form.task_title.trim()) errs.task_title = "Task title is required.";
    if (!form.assigned_to_user_id) errs.assigned_to_user_id = "Please select a user to assign.";
    if (!form.task_date) errs.task_date = "Task date is required.";
    return errs;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    dispatch(
      createDailyTaskAction({
        assigned_by_user_id: currentUserId,
        assigned_to_user_id: form.assigned_to_user_id,
        task_date: form.task_date,
        task_title: form.task_title.trim(),
        task_description: form.task_description.trim(),
        status: "ASSIGNED",
      })
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={1} mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "#1B2559", fontFamily: "DM Sans, sans-serif" }}>
            Assign New Task
          </Typography>
          <Typography variant="body2" sx={{ color: "#A3AED0", fontFamily: "DM Sans, sans-serif" }}>
            Fill in the details to assign a daily task
          </Typography>
        </Box>
      </Stack>

      {/* Form Card */}
      <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid #E8EDF5", p: { xs: 2, md: 4 },  }}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5}>
            {/* Task Title */}
            <Grid item size={{xs:12,sm:4}}>
              <TextField
                label="Task Title *"
                name="task_title"
                value={form.task_title}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.task_title}
                helperText={formErrors.task_title}
                placeholder="Enter task title…"
                sx={FIELD_STYLE}
              />
            </Grid>

            {/* Assign To */}
            <Grid item size={{xs:12,sm:4}}>
              <TextField
                select
                label="Assign To *"
                name="assigned_to_user_id"
                value={form.assigned_to_user_id}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.assigned_to_user_id}
                helperText={formErrors.assigned_to_user_id}
                sx={FIELD_STYLE}
              >
                {users.length === 0 && (
                  <MenuItem disabled value="">No users available</MenuItem>
                )}
                {users.map((u) => (
                  <MenuItem key={u.userId} value={u.userId}>
                    {u.name || u.userId}
                    {u.role ? ` (${u.role})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Task Date */}
            <Grid item size={{xs:12,sm:4}}>
              <TextField
                label="Task Date *"
                name="task_date"
                type="date"
                value={form.task_date}
                onChange={handleChange}
                fullWidth
                error={!!formErrors.task_date}
                helperText={formErrors.task_date}
                InputLabelProps={{ shrink: true }}
                sx={FIELD_STYLE}
              />
            </Grid>

            {/* Description */}
            <Grid item size={{xs:12}}>
              <TextField
                label="Description"
                name="task_description"
                value={form.task_description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Describe the task in detail…"
                sx={FIELD_STYLE}
              />
            </Grid>
          </Grid>

          {/* Actions */}
          <Stack direction="row" justifyContent="flex-end" gap={1.5} mt={4}>
            <Button
              variant="outlined"
              color="inherit"
              disabled={loading}
              onClick={() => navigate(`${basePath}/dailyTask`)}
              sx={{ textTransform: "none", borderRadius: "10px", fontFamily: "DM Sans, sans-serif" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 600,
                bgcolor: "#4318FF",
                "&:hover": { bgcolor: "#3010cc" },
              }}
            >
              {loading ? "Assigning…" : "Assign Task"}
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}
